import { useCallback, useEffect, useRef } from 'react';

/**
 * Пауза перед переходом по КНОПКАМ («Далее», «назад», «вперёд», обложка) —
 * то же поле пака `states.advanceDelayMs`, что у авто-перехода (пачка
 * настроек 1, autoAdvanceDelay.ts). Эталон Slimkit (spec
 * shared/screen-container.md «Переход между экранами» п.1): смена шага через
 * `setTimeout(…, 200)` и для «вперёд», и для «назад»; повторные нажатия
 * блокируются на `200 + 300 = 500 мс`.
 *
 * Пауза 0 (пак без поля) — действие вызывается сразу и синхронно, ровно как
 * было: старые воронки не меняются.
 *
 * Защита от «перехода через один»: таймер помнит экран, на котором нажали.
 * Если к моменту срабатывания экран уже сменился (успел авто-переход, «назад»
 * другой кнопкой) — действие не выполняется.
 */

/** Сколько ещё держать блокировку повторных нажатий после паузы, мс (эталон 300). */
export const NAV_LOCK_TAIL_MS = 300;

export interface DelayedNavigation {
  /**
   * Выполнить действие с паузой. Возвращает результат действия при паузе 0;
   * при паузе — `true`, если нажатие принято, `false`, если проглочено
   * блокировкой.
   */
  run: (action: () => boolean | void) => boolean;
}

export function useDelayedNavigation(
  delayMs: number,
  screenToken: string | undefined,
): DelayedNavigation {
  const tokenRef = useRef(screenToken);
  tokenRef.current = screenToken;
  const lockedUntilRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const run = useCallback(
    (action: () => boolean | void): boolean => {
      if (delayMs <= 0) {
        const result = action();
        return result !== false;
      }
      const now = Date.now();
      if (now < lockedUntilRef.current) return false;
      lockedUntilRef.current = now + delayMs + NAV_LOCK_TAIL_MS;
      const clickedOn = tokenRef.current;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (tokenRef.current !== clickedOn) return;
        action();
      }, delayMs);
      return true;
    },
    [delayMs],
  );

  return { run };
}
